import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import ConfirmInputModal from "Components/organisms/confirm-input-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import IconButton from "Components/atoms/buttons/icon-button"
import { CloseCircleIcon, PencilIcon, PermissionsIcon, ResetPasswordIcon, PlusIcon, HelpIcon, DotsVerticalIcon } from "Components/atoms/icons"
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap"
import useManual from "hooks/useManual"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import UserForm from "Components/organisms/forms/user-form"
import UserFields from "Components/organisms/forms/user-form/types"
import CreateUserForm from "Components/organisms/forms/create-user-form"
import CreateUserFields from "Components/organisms/forms/create-user-form/types"
import EmployeeMatchStatus from "Components/molecule/employee-match-status"
import { CreateUserFormValidation } from "data/create-user-form"
import ResetUserPasswordForm from "Components/organisms/forms/reset-user-password-form"
import ResetUserPasswordFields from "Components/organisms/forms/reset-user-password-form/types"
import { ResetUserPasswordFormValidation } from "data/reset-user-password-form"
import SearchUserForm from "Components/organisms/forms/search-user-form"
import { SelectOption } from "Components/molecule/inputs/select-input"
import { UserFormValidation } from "data/user-form"
import User, { IUserQueryParams } from "models/user"
import UserRole from "models/user-role"
import UserStatus, { USER_STATUS_VARIANT, UserStatuses } from "models/user-status"
import Chip from "Components/atoms/chip"
import Role from "models/role"
import ScreenRole from "models/screen-role"
import {
    AdminUsersSliceState,
    addUser,
    appendToAssignedRole,
    appendToRevokedRole,
    clearRoleChanges,
    setFoucusedUser,
    setQuery,
    setSavingRoles,
    setUser,
    setUserCount,
    setUsers
} from 'slices/admin/users/reducer'
import { assignRoles, EmployeeMatch, getEmployeeMatch, getUserFormFields, getUsers, createUser, resetUserPassword, revokeRoles, submitEditUserForm, terminateUser } from "slices/admin/users/thunk"
import { getRoles } from "slices/admin/roles/thunk"
import { AdminRolesSliceState } from "slices/admin/roles/reducer"
import { ProfileSliceState } from "slices/auth/profile/reducer"
import { toast } from "react-toastify"

// Load every user; the table sorts them client-side by column title
const DEFAULT_SEARCH: IUserQueryParams = {
    page: 0,
    limit: Number.MAX_SAFE_INTEGER
}

// Wait for the admin to stop typing before looking up the employee
const EMPLOYEE_MATCH_DEBOUNCE_MS = 400

const selectAdminUser = createSelector(
    (state: any) => state.AdminUser,
    ({ users, focusedUser, assignedRoles, revokedRoles, savingRoles, userCount, query }: AdminUsersSliceState) => ({
        users, focusedUser, assignedRoles, revokedRoles, savingRoles, userCount, query
    })
)

const selectAdminRole = createSelector(
    (state: any) => state.AdminRoles,
    ({ roles }: AdminRolesSliceState) => ({
        roles
    })
)


const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ user }: ProfileSliceState) => ({
        user
    })
)

const UsersPage = () => {
    document.title = "Users | IbexIQ";
    const { handleOpenManual } = useManual("admin/users");

    const dispatch: any = useDispatch();
    const {
        users,
        focusedUser,
        assignedRoles,
        revokedRoles,
        savingRoles,
        userCount,
        query
    } = useSelector(selectAdminUser)

    const { roles } = useSelector(selectAdminRole)
    const { user } = useSelector(selectProfile)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [editingUser, setEditingUser] = useState<User | null>(null)
    const [terminatingUser, setTerminatingUser] = useState<User | null>(null)
    const [userStatuses, setUserStatuses] = useState<UserStatus[]>([])
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
    // undefined = nothing looked up yet, null = no matching employee
    const [employeeMatch, setEmployeeMatch] = useState<EmployeeMatch | null | undefined>(undefined)
    const [isCheckingMatch, setIsCheckingMatch] = useState<boolean>(false)
    const [resettingUserPassword, setResettingUserPassword] = useState<User | null>(null)

    const fetchUsers = useCallback(async (q: IUserQueryParams) => {
        setIsLoading(true)

        const result = await getUsers(q)
        if (result) {
            dispatch(setUsers(result.data))
            dispatch(setUserCount(result.count))
        }

        setIsLoading(false)
    }, [dispatch])

    useEffect(() => {
        const init = async () => {
            const fields = await getUserFormFields()
            if (fields) setUserStatuses(fields.userStatuses)

            await Promise.all([
                fetchUsers(query),
                dispatch(getRoles())
            ])
        }
        init()
    }, [])

    const handleChangeQuery = <K extends keyof IUserQueryParams>(
        key: K,
        value: IUserQueryParams[K]
    ) => {
        // Search terms wait for Search; every request loads all matching users.
        const q = {
            ...query,
            page: 0,
            limit: Number.MAX_SAFE_INTEGER,
            [key]: value
        }

        dispatch(setQuery(q))
    };

    const handleClearSearch = useCallback(() => {
        const q = { ...DEFAULT_SEARCH }
        dispatch(setQuery(q))
        fetchUsers(q)
    }, [dispatch, fetchUsers]);

    const handleFocusUser = (user: User) => {
        dispatch(setFoucusedUser(user))
    }

    const handleUnfocusUser = () => {
        dispatch(setFoucusedUser(null))
        dispatch(clearRoleChanges())
    }

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        setEditingUser(null)
        resetForm()
    }

    const onSubmit = async (vals: UserFields, helpers: FormikHelpers<UserFields>) => {
        if (!editingUser) return

        helpers.setSubmitting(true)

        const res = await submitEditUserForm(editingUser.id, vals)
        if (res) {
            dispatch(setUser(res))
            handleCloseFormModal()
        }

        helpers.setSubmitting(false)
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        resetForm,
        isSubmitting,
        isValid
    } = useFormik({
        initialValues: {
            userStatusId: ''
        } as UserFields,
        onSubmit,
        validationSchema: UserFormValidation
    })

    const onSubmitCreate = async (vals: CreateUserFields, helpers: FormikHelpers<CreateUserFields>) => {
        helpers.setSubmitting(true)

        const res = await createUser(vals.email, vals.password)
        if (res) {
            dispatch(addUser(res))
            handleCloseCreateModal()
        }

        helpers.setSubmitting(false)
    }

    const createFormik = useFormik({
        initialValues: {
            email: '',
            password: ''
        } as CreateUserFields,
        onSubmit: onSubmitCreate,
        validationSchema: CreateUserFormValidation
    })

    const onSubmitResetPassword = async (vals: ResetUserPasswordFields, helpers: FormikHelpers<ResetUserPasswordFields>) => {
        if (!resettingUserPassword) return

        helpers.setSubmitting(true)

        const success = await resetUserPassword(resettingUserPassword.id, vals.password, vals.sendEmail)
        if (success) handleCloseResetPasswordModal()

        helpers.setSubmitting(false)
    }

    const resetPasswordFormik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: '',
            sendEmail: false
        } as ResetUserPasswordFields,
        onSubmit: onSubmitResetPassword,
        validationSchema: ResetUserPasswordFormValidation
    })

    const handleOpenResetPasswordModal = (user: User) => setResettingUserPassword(user)

    const handleCloseResetPasswordModal = () => {
        setResettingUserPassword(null)
        resetPasswordFormik.resetForm()
    }

    const handleOpenCreateModal = () => setShowCreateModal(true)

    const handleCloseCreateModal = () => {
        setShowCreateModal(false)
        createFormik.resetForm()
        setEmployeeMatch(undefined)
    }

    // Look up the employee this email will link to once the admin stops typing
    const createEmail = createFormik.values.email
    const createEmailError = createFormik.errors.email
    useEffect(() => {
        setEmployeeMatch(undefined)
        if (!createEmail || createEmailError) {
            setIsCheckingMatch(false)
            return
        }

        let cancelled = false
        setIsCheckingMatch(true)

        const timeout = setTimeout(async () => {
            const match = await getEmployeeMatch(createEmail)
            if (cancelled) return

            setEmployeeMatch(match)
            setIsCheckingMatch(false)
        }, EMPLOYEE_MATCH_DEBOUNCE_MS)

        return () => {
            cancelled = true
            clearTimeout(timeout)
        }
    }, [createEmail, createEmailError])

    const handleTerminateUser = (user: User) => setTerminatingUser(user)

    const handleCloseTerminateModal = () => setTerminatingUser(null)

    const confirmTerminateUser = async () => {
        if (!terminatingUser) return

        const res = await terminateUser(terminatingUser.id)
        if (res) {
            dispatch(setUser(res))
            handleCloseTerminateModal()
        }
    }

    const handleEditUser = (user: User) => {
        setEditingUser(user)
        setValues({ userStatusId: user.userStatusId }, false)
        setShowFormModal(true)
    }

    const userStatusOptions: SelectOption[] = useMemo(() => userStatuses.map(s => ({
        value: s.id, label: s.description
    })), [userStatuses])

    // Get all current user roles
    const focusedUserRoles = useMemo(() => {
        if (focusedUser?.userRoles) {
            return focusedUser.userRoles.filter((uR: UserRole) => !!uR.role).map((userRole: UserRole) => userRole.role)
        }
        return []
    }, [focusedUser]) as Role[]

    const handleSaveRoleChanges = async (assigned: Role[], revoked: Role[], user: User) => {
        dispatch(setSavingRoles(true))
        const assignRes = await dispatch(assignRoles(assigned, user))
        const revokeRes = await dispatch(revokeRoles(revoked, user))
        dispatch(setSavingRoles(false))

        if (!assignRes || !revokeRes) {
            toast.error("Something went wrong. Please try again.")
        } else {
            toast.success("Roles updated")
        }

        dispatch(setFoucusedUser(null))
        dispatch(clearRoleChanges())
    }

    const columns = React.useMemo<ColumnDef<User>[]>(() => [
        {
            header: "Status",
            id: "status",
            size: 110,
            accessorFn: (u) => u.userStatus?.description,
            cell: ({ row }) => {
                const status = row.original.userStatus
                if (!status) return "—"

                return <Chip variant={USER_STATUS_VARIANT[status.description]}>
                    {status.description}
                </Chip>
            }
        },
        {
            header: "Email",
            accessorKey: "email",
            size: 260,
        },
        {
            header: "Roles",
            id: "roles",
            size: 250,
            accessorFn: (u) => u.userRoles?.map((uR: UserRole) => uR.role?.name).join(", ") ?? "",
            cell: ({ row }) => <div className="d-flex flex-wrap gap-1 py-1">
                {row.original.userRoles?.map((uR: UserRole) => uR.role?.name && <Chip key={uR.role.name} variant="primary">{uR.role.name}</Chip>)}
            </div>,
            // Users can hold several roles, so wrap instead of truncating to one line.
            meta: { cellClassName: "text-wrap overflow-visible" }
        },
        {
            // Same menu for every user: each action has an icon and a label; only what applies to the status shows
            header: "",
            id: "actions",
            size: 70,
            enableSorting: false,
            enableHiding: false,
            meta: { cellClassName: "overflow-visible text-end" },
            cell: ({ row }) => {
                const u = row.original
                const canResetPassword = u.userStatus?.description !== UserStatuses.Inactive && u.id !== user?.id
                return <UncontrolledDropdown>
                    <DropdownToggle tag="button" type="button" className="btn btn-sm btn-light" title="Actions">
                        <DotsVerticalIcon />
                    </DropdownToggle>
                    <DropdownMenu end container="body" className="dropdown-menu--portal">
                        <DropdownItem onClick={() => handleEditUser(u)}><PencilIcon className="me-2" />Edit</DropdownItem>
                        <DropdownItem onClick={() => handleFocusUser(u)}><PermissionsIcon className="me-2 text-info" />Permissions</DropdownItem>
                        {canResetPassword && <DropdownItem onClick={() => handleOpenResetPasswordModal(u)}><ResetPasswordIcon className="me-2" />Reset Password</DropdownItem>}
                        <DropdownItem divider />
                        <DropdownItem className="text-danger" onClick={() => handleTerminateUser(u)}><CloseCircleIcon className="me-2 text-danger" />Terminate</DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            }
        }
    ], [user])

    const table = useDataTable({
        columns,
        data: users,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    // Roles the focused user holds, kept in sync with the pending assign/revoke changes.
    const isAssigned = useCallback((role: Role) => {
        if (revokedRoles.some((r: Role) => r.id === role.id)) return false
        if (assignedRoles.some((r: Role) => r.id === role.id)) return true

        return focusedUserRoles.some((r: Role) => r.id === role.id)
    }, [assignedRoles, revokedRoles, focusedUserRoles])

    const roleColumns = React.useMemo<ColumnDef<Role>[]>(() => [
        {
            header: "Assigned",
            id: "assigned",
            size: 90,
            enableSorting: false,
            cell: ({ row }) => <input
                type="checkbox"
                checked={isAssigned(row.original)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                        dispatch(appendToAssignedRole(row.original))
                    } else {
                        dispatch(appendToRevokedRole(row.original))
                    }
                }}
            />
        },
        {
            header: "Name",
            accessorKey: "name",
            size: 220
        },
        {
            header: "Screens",
            id: "screens",
            size: 500,
            accessorFn: (r) => r.screenRoles?.map((sR: ScreenRole) => sR.screen?.name).join(", ") ?? "",
            cell: ({ row }) => row.original.screenRoles?.map((sR: ScreenRole) => sR.screen?.name).join(", "),
            // Roles carry a long screen list, so wrap it instead of truncating to one line.
            meta: { cellClassName: "text-wrap overflow-visible" }
        }
    ], [isAssigned])

    const roleTable = useDataTable({
        columns: roleColumns,
        data: roles,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    if (focusedUser) {
        return <DataTableTemplate
            title="Admin"
            breadcrumbItem={`Users / ${focusedUser.email}`}
            table={roleTable}
            columns={roleColumns}
            isLoading={false}
            actions={<>
                {(assignedRoles.length > 0 || revokedRoles.length > 0) && <PrimaryButton
                    onClick={() => handleSaveRoleChanges(assignedRoles, revokedRoles, focusedUser)}
                    disabled={savingRoles}
                >
                    {savingRoles ? 'Saving...' : 'Save'}
                </PrimaryButton>}
                {!savingRoles && <SecondaryButton onClick={handleUnfocusUser}>Back</SecondaryButton>}
                <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
            </>}
        />
    }

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Users"
        table={table}
        columns={columns}
        isLoading={isLoading}
        loadingTitle="Loading Users"
        rowCount={userCount}
        actions={<>
            <ActionButton onClick={handleOpenCreateModal}>
                Create User <PlusIcon />
            </ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        filters={<SearchUserForm
            handleChangeQuery={handleChangeQuery}
            handleClearSearch={handleClearSearch}
            handleSearch={() => fetchUsers(query)}
            roles={[{ value: '', label: 'All' }, ...roles.map((r: Role) => ({ value: r.id, label: r.name }))]}
            userStatuses={[{ value: '', label: 'All' }, ...userStatusOptions]}
            query={query}
            isSearching={isLoading}
        />}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title="Edit User"
                subtitle={editingUser?.email}
            >
                <UserForm
                    edit
                    userStatuses={userStatusOptions}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isValid={isValid}
                    isSubmitting={isSubmitting}
                />
            </Modal>
            <Modal
                isOpen={showCreateModal}
                onClose={handleCloseCreateModal}
                title="Create User"
            >
                <CreateUserForm
                    values={createFormik.values}
                    errors={createFormik.errors}
                    touched={createFormik.touched}
                    handleChange={createFormik.handleChange}
                    handleBlur={createFormik.handleBlur}
                    handleSubmit={createFormik.handleSubmit}
                    isValid={createFormik.isValid && createFormik.dirty}
                    isSubmitting={createFormik.isSubmitting}
                    emailStatus={(isCheckingMatch || employeeMatch !== undefined) && <EmployeeMatchStatus
                        match={employeeMatch}
                        isChecking={isCheckingMatch}
                    />}
                />
            </Modal>
            <Modal
                isOpen={!!resettingUserPassword}
                onClose={handleCloseResetPasswordModal}
                title="Reset Password"
                subtitle={resettingUserPassword?.email}
            >
                <ResetUserPasswordForm
                    values={resetPasswordFormik.values}
                    errors={resetPasswordFormik.errors}
                    touched={resetPasswordFormik.touched}
                    handleChange={resetPasswordFormik.handleChange}
                    handleBlur={resetPasswordFormik.handleBlur}
                    handleSubmit={resetPasswordFormik.handleSubmit}
                    isValid={resetPasswordFormik.isValid && resetPasswordFormik.dirty}
                    isSubmitting={resetPasswordFormik.isSubmitting}
                />
            </Modal>
            <ConfirmInputModal
                isOpen={!!terminatingUser}
                onModalClose={handleCloseTerminateModal}
                modalTitle="Terminate User"
                subTitle={`${terminatingUser?.email} will be set to Inactive and lose every role and department permission. This cannot be undone.`}
                confirmWord="terminate"
                handleAccept={confirmTerminateUser}
                acceptText="Terminate"
                processingText="Terminating..."
            />
        </>}
    />
}

export default UsersPage
